/**
 * Buffers complete browser operations and emits each root trace as one JSON line.
 *
 * Nested synchronous calls and promises created inside the same callback share
 * an ordered record buffer. Original thrown values are always rethrown.
 */
type LogLevel = 'debug' | 'info' | 'notice' | 'warning' | 'error'
type Operation<TResult> = () => TResult

type ErrorRecord = {
  type: string
  solution: string
  stack: string
}

type LogRecord = {
  time: string
  level: LogLevel
  depth: number
  source: string
  method: string
  message: string
  duration: number | null
  error?: ErrorRecord
  records?: LogRecord[]
}

type LogBuffer = {
  records: LogRecord[]
  flushed: boolean
}

type ActiveOperation = {
  buffer: LogBuffer
  depth: number
}

type SharedLogContext = {
  activeOperations: ActiveOperation[]
  promiseContextInstalled: boolean
}

// Each page loads several independently bundled entries. They share one
// operation stack and one Promise patch so continuations are wrapped once.
const sharedGlobal = globalThis as typeof globalThis & { __dishBookLogContext?: SharedLogContext }
const sharedContext = sharedGlobal.__dishBookLogContext ??= {
  activeOperations: [],
  promiseContextInstalled: false,
}
const activeOperations = sharedContext.activeOperations
const activeRootBuffers = new Set<LogBuffer>()
const loggedExceptions = new WeakSet<object>()
let shutdownHooksRegistered = false

installPromiseContextPropagation()

/**
 * Carries the active log buffer through Promise continuations, patching
 * Promise.prototype.then once per page whatever bundle loads first. Production
 * bundles lower async functions to Promise chains, so work after each await
 * resumes in the originating root without merging concurrent operations.
 */
function installPromiseContextPropagation(): void {
  if (sharedContext.promiseContextInstalled) {
    return
  }

  sharedContext.promiseContextInstalled = true
  const originalThen = Promise.prototype.then

  Promise.prototype.then = function <TResult1 = unknown, TResult2 = never>(
    onFulfilled?: ((value: unknown) => TResult1 | PromiseLike<TResult1>) | null,
    onRejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    const context = activeOperations.at(-1)
    const fulfilled = bindContinuation(context, onFulfilled)
    const rejected = bindContinuation(context, onRejected)

    return originalThen.call(this, fulfilled, rejected) as Promise<TResult1 | TResult2>
  }
}

/**
 * Wraps one Promise continuation with its captured operation, preserving the
 * callback value, thrown failure, and concurrent root separation.
 */
function bindContinuation<TResult>(
  context: ActiveOperation | undefined,
  continuation?: ((value: unknown) => TResult | PromiseLike<TResult>) | null,
): ((value: unknown) => TResult | PromiseLike<TResult>) | null | undefined {
  if (context === undefined || continuation === undefined || continuation === null) {
    return continuation
  }

  return (value: unknown) => withActiveOperation(
    context,
    () => continuation(value),
  )
}

/**
 * Restores one captured operation only for the duration of its continuation
 * and always removes it after success or failure.
 */
function withActiveOperation<TResult>(
  context: ActiveOperation,
  operation: () => TResult,
): TResult {
  activeOperations.push(context)

  try {
    return operation()
  } finally {
    removeActiveOperation(context)
  }
}

/** Records sanitized implementation detail around an optional operation. */
function debug<TResult = undefined>(
  message: string,
  operation?: Operation<TResult>,
  solutionForError?: string,
): TResult {
  return record('debug', message, operation, solutionForError) as TResult
}

/** Records retrieval, observation, or calculation around an optional operation. */
function info<TResult = undefined>(
  message: string,
  operation?: Operation<TResult>,
  solutionForError?: string,
): TResult {
  return record('info', message, operation, solutionForError) as TResult
}

/** Records an attempted action or state change around an optional operation. */
function notice<TResult = undefined>(
  message: string,
  operation?: Operation<TResult>,
  solutionForError?: string,
): TResult {
  return record('notice', message, operation, solutionForError) as TResult
}

/** Records a recoverable problem or operation while preserving callback behavior. */
function warning<TResult = undefined>(
  message: string,
  operation?: Operation<TResult>,
  solutionForError?: string,
): TResult {
  return record('warning', message, operation, solutionForError) as TResult
}

/** Records a failed required operation with a practical developer solution. */
function error<TResult = undefined>(
  message: string,
  operation?: Operation<TResult>,
  solutionForError?: string,
): TResult {
  return record('error', message, operation, solutionForError) as TResult
}

/**
 * Reserves and finalizes one record around a synchronous or asynchronous callback.
 */
function record<TResult>(
  level: LogLevel,
  message: string,
  operation?: Operation<TResult>,
  solutionForError?: string,
): TResult | undefined {
  registerShutdownHooks()

  const parent = activeOperations.at(-1)
  const buffer = parent?.buffer ?? { records: [], flushed: false }
  const isRoot = parent === undefined
  const location = caller()
  const entry: LogRecord = {
    time: new Date().toISOString(),
    level,
    depth: parent === undefined ? 0 : parent.depth + 1,
    source: location.source,
    method: location.method,
    message: sanitize(message),
    duration: null,
  }

  buffer.records.push(entry)

  if (isRoot) {
    activeRootBuffers.add(buffer)
  }

  if (operation === undefined) {
    if (level === 'error') {
      const reported = new Error(entry.message)
      entry.error = {
        type: 'ErrorReported',
        solution: sanitize(
          solutionForError
            ?? 'Inspect the browser stack, correct the failed operation, and retry.',
        ),
        stack: sanitizeStack(reported.stack ?? reported.message),
      }
    }

    if (isRoot) {
      flushBuffer(buffer)
    }

    return undefined
  }

  const startedAt = performance.now()
  const active = { buffer, depth: entry.depth }
  activeOperations.push(active)

  try {
    const result = operation()

    if (isPromiseLike(result)) {
      removeActiveOperation(active)

      return Promise.resolve(result).then(
        (value) => {
          entry.duration = duration(startedAt)

          if (isRoot) {
            flushBuffer(buffer)
          }

          return value
        },
        (thrown: unknown) => {
          finalizeFailure(entry, message, thrown, solutionForError, startedAt)

          if (isRoot) {
            flushBuffer(buffer)
          }

          throw thrown
        },
      ) as TResult
    }

    entry.duration = duration(startedAt)

    return result
  } catch (thrown: unknown) {
    finalizeFailure(entry, message, thrown, solutionForError, startedAt)

    throw thrown
  } finally {
    removeActiveOperation(active)

    if (isRoot && !isPromiseLikeResultPending(entry)) {
      flushBuffer(buffer)
    }
  }
}

/** Finalizes one failed record without replacing or obscuring the thrown value. */
function finalizeFailure(
  entry: LogRecord,
  message: string,
  thrown: unknown,
  solutionForError: string | undefined,
  startedAt: number,
): void {
  rememberException(thrown)

  const exception = normalizeError(thrown)
  entry.level = 'error'
  entry.message = sanitize(`${message}: ${exception.message}`)
  entry.duration = duration(startedAt)
  entry.error = {
    type: stableErrorType(exception),
    solution: sanitize(
      solutionForError
        ?? 'Inspect the exception stack, correct the failed operation, and retry.',
    ),
    stack: sanitizeStack(exception.stack ?? `${exception.name}: ${exception.message}`),
  }
}

/** Converts any thrown JavaScript value into an Error without losing its description. */
function normalizeError(thrown: unknown): Error {
  if (thrown instanceof Error) {
    return thrown
  }

  let description: string = typeof thrown

  try {
    description = String(thrown)
  } catch (error) {
    const conversionFailure = error instanceof Error
      ? error.stack ?? `${error.name}: ${error.message}`
      : typeof error

    description = `unprintable thrown value (${conversionFailure})`
  }

  return new Error(description)
}

/** Reports whether this exact object failure was already retained by a log operation. */
export function hasException(exception: unknown): boolean {
  return isObjectLike(exception) && loggedExceptions.has(exception)
}

/** Reports whether the current synchronous execution is inside a buffered operation. */
export function operationActive(): boolean {
  return activeOperations.length > 0
}

/** Remembers object failures so shared handlers can avoid duplicate diagnostics. */
function rememberException(exception: unknown): void {
  if (isObjectLike(exception)) {
    loggedExceptions.add(exception)
  }
}

/** Flushes all incomplete roots during supported browser shutdown notifications. */
export function flush(): void {
  for (const buffer of [...activeRootBuffers]) {
    flushBuffer(buffer)
  }
}

/** Registers browser lifecycle hooks once without altering application shutdown behavior. */
function registerShutdownHooks(): void {
  if (shutdownHooksRegistered || typeof globalThis.addEventListener !== 'function') {
    return
  }

  shutdownHooksRegistered = true
  globalThis.addEventListener('pagehide', flush)
  globalThis.addEventListener('unhandledrejection', flush)
  globalThis.addEventListener('error', flush)
}

/** Emits one root record containing its complete ordered nested operation records. */
function flushBuffer(buffer: LogBuffer): void {
  if (buffer.flushed || buffer.records.length === 0) {
    return
  }

  buffer.flushed = true
  activeRootBuffers.delete(buffer)

  const [root, ...nested] = buffer.records

  if (root === undefined) {
    return
  }

  if (nested.length > 0) {
    root.records = nested
  }

  const severity: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    notice: 2,
    warning: 3,
    error: 4,
  }

  for (const record of nested) {
    if (severity[record.level] > severity[root.level]) {
      root.level = record.level
    }
  }

  try {
    const encoded = JSON.stringify(root)

    if (root.level === 'error') {
      console.error(encoded)
    } else if (root.level === 'warning') {
      console.warn(encoded)
    } else {
      console.info(encoded)
    }
  } catch (error) {
    const outputFailure = normalizeError(error)

    console.error(JSON.stringify({
      time: new Date().toISOString(),
      level: 'error',
      depth: 0,
      source: 'resources/ts/core/logger.ts',
      method: 'emit',
      message: 'Could not emit the completed browser log buffer.',
      duration: 0,
      error: {
        type: stableErrorType(outputFailure),
        solution: 'Inspect the browser console implementation and serialized log values.',
        stack: sanitizeStack(
          outputFailure.stack ?? `${outputFailure.name}: ${outputFailure.message}`,
        ),
      },
    }))
  }
}

/** Finds the first caller frame outside this module and extracts method and location. */
function caller(): { source: string; method: string } {
  const stack = new Error().stack ?? ''
  const frames = stack.split('\n').map((line) => line.trim())

  for (const frame of frames) {
    if (
      frame === ''
      || frame === 'Error'
      || frame.includes('/core/logger.')
      || frame.includes('record (')
    ) {
      continue
    }

    const match = frame.match(/^at\s+(?:(.*?)\s+\()?(.+?):(\d+):(\d+)\)?$/)

    if (match !== null) {
      return {
        source: relativeSource(`${match[2]}:${match[3]}`),
        method: match[1] || 'global',
      }
    }
  }

  return { source: 'unknown:0', method: 'global' }
}

/** Makes a browser source location compact while preserving its line number. */
function relativeSource(source: string): string {
  const markers = ['/resources/', '/public/assets/']

  for (const marker of markers) {
    const index = source.indexOf(marker)

    if (index >= 0) {
      return source.slice(index + 1)
    }
  }

  return source
}

/** Removes the active synchronous context without disturbing an outer operation. */
function removeActiveOperation(operation: ActiveOperation): void {
  const index = activeOperations.lastIndexOf(operation)

  if (index >= 0) {
    activeOperations.splice(index, 1)
  }
}

/** Recognizes native and cross-realm promise-like callback results. */
function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return isObjectLike(value)
    && 'then' in value
    && typeof value.then === 'function'
}

/** Distinguishes callbacks that are still awaiting asynchronous finalization. */
function isPromiseLikeResultPending(entry: LogRecord): boolean {
  return entry.duration === null
}

/** Recognizes values that WeakSet and promise inspection can safely accept. */
function isObjectLike(value: unknown): value is object {
  return (
    (typeof value === 'object' && value !== null)
    || typeof value === 'function'
  )
}

/** Converts monotonic elapsed time into stable milliseconds. */
function duration(startedAt: number): number {
  return Number((performance.now() - startedAt).toFixed(3))
}

/** Converts an Error name into the stable external error-type convention. */
function stableErrorType(error: Error): string {
  const name = error.name || error.constructor.name || 'Runtime'

  return name.startsWith('Error') ? name : `Error${name}`
}

/** Removes secrets, share and invitation tokens, URL query values, control whitespace, and surrounding space. */
function sanitize(value: string): string {
  return value
    .trim()
    .replace(/[\r\n\t]+/gu, ' ')
    .replace(/(https?:\/\/[^/\s]+)(?:\/\S*)?/giu, '$1/[redacted]')
    .replace(/(?:\/data|\/app\/data)\/[^\s:]+/gu, '[data-path]')
    .replace(/\/(share|invite)\/[^/\s?#:]+/giu, '/$1/[redacted]')
    .replace(/([?&][^=\s]+)=([^&\s]+)/gu, '$1=[redacted]')
    .replace(
      /\b(password|secret|token|api[_ -]?key|cookie|authorization)\s*[:=]\s*\S+/giu,
      '$1=[redacted]',
    )
}

/** Preserves stack line structure for JSON escaping while redacting secrets. */
function sanitizeStack(value: string): string {
  return value
    .trim()
    .replace(/(https?:\/\/[^/\s]+)(?:\/\S*)?/giu, '$1/[redacted]')
    .replace(/(?:\/data|\/app\/data)\/[^\s:]+/gu, '[data-path]')
    .replace(/\/(share|invite)\/[^/\s?#:]+/giu, '/$1/[redacted]')
    .replace(/([?&][^=\s]+)=([^&\s]+)/gu, '$1=[redacted]')
    .replace(
      /\b(password|secret|token|api[_ -]?key|cookie|authorization)\s*[:=]\s*\S+/giu,
      '$1=[redacted]',
    )
}

export const Log = {
  debug,
  info,
  notice,
  warning,
  error,
  hasException,
  operationActive,
  flush,
}
